<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon
        small
        class="ml-1"
        v-on="on"
      >
        <v-icon color="hrissecondary">
          mdi-content-copy
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title=" Create New Approval Workflow"
      text="Revise Approval Workflow"
      class="mb-0"
    > 
      <v-container v-if="dialog && !loading">
        <v-row no-gutters>
          <v-col
            cols="6"
            class="py-0 pr-3"
          >
            <v-text-field
              v-model="totalApprovalLevel"
              v-validate="'required|min_value:1'"
              :min="1"
              :error-messages="errors.collect('totalApprovalLevel')"
              type="number"
              data-vv-name="totalApprovalLevel"
              data-vv-as="total level of approval"
              label="Total Level of Approval"
              @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
            />
          </v-col>

          <v-col
            cols="6"
            class="py-0 pl-3"
          >
            <v-text-field
              v-model="superiorApprovalLevel"
              v-validate="'required|min_value:1|maxIsTotalApprovalLevel'"
              :min="1"
              :max="totalApprovalLevel"
              :error-messages="errors.collect('superiorApprovalLevel')"
              type="number"
              data-vv-name="superiorApprovalLevel"
              data-vv-as="superior's approval level"
              label="Superior's Approval Level"
              @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
            />
          </v-col>

          <v-col
            cols="12"
            class="py-0 pr-3"
          >
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="startDate"
                  v-validate="'required'"
                  label="Start Date"
                  :error-messages="errors.collect('startDate')"
                  data-vv-name="startDate"  
                  data-vv-as="start date"    
                  readonly
                  class="label-position"
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                no-title
                :min="tomorrow"
                scrollable
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col
            cols="12"
            class="py-0 mt-n3"
          >
            <v-row>
              <v-col>
                <span class="font-weight-bold pr-2">Approval Workflows (Drag approval levels in the correct order or change the superior's approval level):</span>
              </v-col>
            </v-row>

            <draggable
              v-model="workflows"
              @end="onDragEnd"
            >
              <v-row
                v-for="(workflow, index) in workflows"
                :key="index"
                no-gutters
                class="px-3 grab"
              >
                <v-col>
                  <v-card 
                    outlined 
                    class="px-2 pt-1 mb-2"
                  >
                    <div class="font-weight-bold mb-2">
                      Approval Level {{ workflow.level }}
                    </div>
                    <v-row no-gutters>
                      <v-col
                        cols="2"
                        class="pa-0 ml-4"
                      >
                        <v-text-field
                          v-model="workflow.approving_verb"
                          v-validate="'required'"
                          :name="'approving_verb' + index"
                          :error-messages="errors.collect('approving_verb' + index)"
                          label="Approving Verb"
                          autocomplete="off"
                          data-vv-as="approving verb"
                        />
                      </v-col>

                      <v-col
                        class="pa-0 mx-4"
                      >
                        <v-autocomplete
                          v-model="workflow.type"
                          v-validate="'required'"
                          :name="'approvable_type' + index"
                          :error-messages="errors.collect('approvable_type' + index)"
                          data-vv-delay="500"
                          data-vv-as="approvable type"
                          :items="workflow.type !== 'Superior' ? types.filter(t => t !== 'Superior') : types"
                          label="Approvable Type"
                          autocomplete="off"
                          :readonly="workflow.type === 'Superior'"
                        />
                      </v-col>

                      <v-col 
                        v-if="workflow.type && workflow.type !== 'Superior'"
                        class="pa-0 mr-4"
                      >
                        <v-autocomplete
                          v-model="workflow.approvable_id"
                          v-validate="workflow.type !== 'Superior' ? 'required' : ''"
                          :name="'approvable_id' + index"
                          :error-messages="errors.collect('approvable_id' + index)"
                          data-vv-delay="500"
                          :data-vv-as="workflow.type"
                          :items="getItems(workflow.type)"
                          :label="workflow.type"
                          :item-text="workflow.type === 'Specific Employee' ? 'person.name' : 'name'"
                          :item-value="workflow.type === 'Specific Employee' ? 'employee_uid' : 'id'"
                        />
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </draggable>
          </v-col>
        </v-row>
        
        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="progressing"
            text
            class="px-10"
            @click="dialog = false"
          >
            Close
          </v-btn>
          <v-btn
            :loading="progressing"
            color="hrissecondary"   
            class="white--text px-10"       
            @click="submitForm"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-container>
      <v-container
        v-else
        style="height: 150px;"
      >
        <v-row
          class="fill-height"
          align="center" 
          justify="center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </v-row>     
      </v-container>
    </material-card>
  </v-dialog>
</template>

<script>
  import draggable from 'vuedraggable'
  export default {
    components: {
      draggable,
    },
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      approvalWorkflowBatch: {
        type: Object,
        required: true,
      },
      employees: {
        type: Array,
        required: true,
      },
      departments: {
        type: Array,
        required: true,
      },
      positions: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,        
        loading: false,
        progressing: false,
        startDateMenu: false,
        startDate: null,
        tomorrow: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().slice(0, 10),
        localClaimApprovalWorkflowBatch: null,
        workflows: [],
        types: ['Superior', 'Department', 'Position', 'Specific Employee'],
        totalApprovalLevel: null,
        superiorApprovalLevel: null,
      }
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.localClaimApprovalWorkflowBatch = { ...this.approvalWorkflowBatch }
        this.totalApprovalLevel = this.localClaimApprovalWorkflowBatch.total_level_of_approval
        this.superiorApprovalLevel = this.localClaimApprovalWorkflowBatch.superior_approval_level

        this.workflows = _.orderBy(this.localClaimApprovalWorkflowBatch.claim_approval_workflows.map(workflow => {
          const type = this.getType(workflow.approvable_type)

          return {
            level: workflow.approval_level,
            approving_verb: workflow.approving_verb,
            type: type,
            approvable_id: workflow.approvable_id ? (type === 'Specific Employee' ? workflow.approvable_id : workflow.approvable.id) : null,
          }
        }), ['approval_level'], ['asc'])

        let scope = this

        this.$validator.extend('maxIsTotalApprovalLevel', {
          getMessage (field, args) {
            return 'Cannot enter level greater than total approval level'
          },
          validate (value, args) {
            const numValue = Number(value)
            const maxLevel = Number(scope.totalApprovalLevel)

            return numValue <= maxLevel
          },
        })
      },
      totalApprovalLevel (newVal) {
        const level = parseInt(newVal) || 0

        // Add new rows
        while (this.workflows.length < level) {
          this.workflows.push({
            level: this.workflows.length + 1,
            type: '',
            approvable_id: '',
          })
        }

        // Trim extra rows
        if (this.workflows.length > level) {
          this.workflows.length = level
        }

        this.syncSuperior()
      },
      superiorApprovalLevel () {
        this.syncSuperior()
      },
    },
    methods: {
      onDragEnd () {
        // Recalculate all approval levels based on new order
        this.workflows.forEach((workflow, index) => {
          workflow.level = index + 1
        })

        // Update superiorApprovalLevel if Superior row moved
        const superior = this.workflows.find(w => w.type === 'Superior')
        if (superior) {
          this.superiorApprovalLevel = superior.level
        }

        // Sync Superior row again after order changes
        this.syncSuperior()
      },
      syncSuperior () {
        this.workflows.forEach((w, i) => {
          w.level = i + 1

          if (w.level === parseInt(this.superiorApprovalLevel)) {
            // Force this row to be Superior
            w.type = 'Superior'
            w.approvable_id = null
          } else if (w.type === 'Superior') {
            // Prevent other rows from being Superior
            w.type = ''
            w.approvable_id = ''
          }
        })
      },
      getType (approvable) {
        switch (approvable) {
          case 'App\\Model\\Department':
            return 'Department'
          case 'App\\Model\\Position':
            return 'Position'
          case 'App\\Model\\Employee':
            return 'Specific Employee'
          default:
            return 'Superior'
        }
      },
      getItems (type) {
        switch (type) {
          case 'Department':
            return this.departments
          case 'Position':
            return this.positions
          case 'Specific Employee':
            return this.employees
          default:
            return null
        }
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true
    
            this.$api.claimApprovalWorkflowBatches().copy(this.approvalWorkflowBatch.id, this.prepareFormData())
              .then((res) => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('updated')              
                  this.$snack({ message: 'New approval workflow created' })
                  this.$resetData()
                  this.dialog = false                 
                }
              })
              .catch(error => {
                if (error.response.data.message) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.message, 
                  })
                } else {
                  this.$snack({
                    color: 'error',
                    message: 'Failed to revise approval workflow',
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        return {
          total_level_of_approval: this.totalApprovalLevel,
          superior_approval_level: this.superiorApprovalLevel,
          start_date: this.startDate,
          workflows: this.workflows,
        }
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}

.grab {
  cursor: grab;
}
</style>
